import { MatSelectModule } from '@angular/material/select'
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatIconModule } from '@angular/material/icon'
import { Store } from '@ngrx/store'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import {
	CheckinActions,
	CheckinDate,
	ClientActions,
	VenueActions,
	getCheckinAvailabilities,
	getCheckinStatusAvailabilities,
	getCheckinStatusConfirm,
	getClient,
	getSelectedVenue,
} from '@monorepo-channels/channels/domain'
import { ActivatedRoute, Router, RouterOutlet, RouterLink } from '@angular/router'
import { combineLatest, map, take } from 'rxjs'
import { CommonModule, Location } from '@angular/common'
import { Actions, ofType } from '@ngrx/effects'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import {
	ConfirmSliderUiComponent,
	LoadingPageComponent,
	OrderSuccessComponent,
} from '@monorepo-channels/components/ui-porto-bank'
import { MatDialog } from '@angular/material/dialog'
import { CheckinNotAcceptableComponent } from '../dialog/checkin-not-acceptable.component'
import { DataLayerService, isMobile, MenuOpened } from '@monorepo-channels/shared/util-helpers'

@Component({
	selector: 'feature-porto-bank-checkin',
	standalone: true,
	imports: [
		CommonModule,
		MatIconModule,
		MatButtonModule,
		RouterOutlet,
		FormsModule,
		MatProgressBarModule,
		ReactiveFormsModule,
		RouterLink,
		MatSelectModule,
		MatOptionModule,
		MatFormFieldModule,
		ConfirmSliderUiComponent,
		OrderSuccessComponent,
		LoadingPageComponent,
	],
	templateUrl: './checkin.component.html',
	styleUrls: ['./checkin.component.scss'],
})
export class CheckinComponent implements OnInit {
	public confirm = signal(false)
	public eula = signal(false)
	public menuOpened = inject(MenuOpened)
	public eulaFromClient = signal(false)
	private store = inject(Store)
	private actions$ = inject(Actions)
	private router = inject(Router)
	public location = inject(Location)
	private route = inject(ActivatedRoute)
	public isDisabled = computed(() => !this.confirm() || (this.showTerms() && !this.eula()))
	public partysize = signal(1)

	// Remover após campanha The Taste
	private readonly priorityVenueIds = [
		'6a0cb18f3cde04c3051e85ca', // Ristorantino
		'6a0cb1863cde04c3051e82f1', // Casa Rios
		'5bae5c12fa271c9ee3c54fe2', // Ristorantino para teste em STG
	]
	public isFixedPartySize = false
	public checkinDate = new CheckinDate()
	public showTerms = signal(false)
	public activeTab = signal(1)
	private dialog = inject(MatDialog)
	private dataLayerService = inject(DataLayerService)
	isMobile = isMobile()

	private venueId: string | null = null
	vm$ = combineLatest([
		this.store.select(getClient),
		this.store.select(getCheckinStatusAvailabilities),
		this.store.select(getCheckinAvailabilities),
		this.store.select(getCheckinStatusConfirm),
		this.store.select(getSelectedVenue),
	]).pipe(
		map(([client, statusAvailability, availabilities, statusConfirm, venue]) => ({
			client,
			statusAvailability,
			availabilities,
			statusConfirm,
			venue,
		}))
	)
	public vm = toSignal(this.vm$, { initialValue: null })

	handleConfirmChange(event: Event) {
		const input = event.target as HTMLInputElement | null
		if (input) {
			this.confirm.set(input.checked)
		}
	}
	public toggleTermsAndConditions(value: boolean) {
		this.eula.set(value)
	}

	constructor() {
		this.store
			.select(getClient)
			.pipe(takeUntilDestroyed())
			.subscribe(client => {
				const hasAcceptedEula = client?.agreements?.eula ?? false
				this.showTerms.set(!hasAcceptedEula)

				this.eulaFromClient.set(hasAcceptedEula)
				this.eula.set(hasAcceptedEula)
			})

		this.actions$.pipe(takeUntilDestroyed(), ofType(CheckinActions.checkInConfirmFailure)).subscribe({
			next: action => {
				this.confirm.set(false)
				if (action.type === '[Checkin Page] CheckIn Confirm Failure') {
					this.dialog.open(CheckinNotAcceptableComponent)
				}
			},
		})

		effect(
			() => {
				const statusConfirm = this.vm()?.statusConfirm

				if (statusConfirm === 'success') {
					this.activeTab.set(3)
				} else if (!this.isDisabled()) {
					this.activeTab.set(2)
				} else {
					this.activeTab.set(1)
				}
			},
			{ allowSignalWrites: true }
		)
	}

	ngOnInit(): void {
		this.dataLayerService.logEvent({
			event: 'screen_view',
			screen_name: 'Gastronomia Porto Bank - Check-in',
			venueId: this.venueId,
		})

		this.store.dispatch(CheckinActions.checkinReset())
		this.venueId = this.route.snapshot.paramMap.get('venueId')

		// Remover após campanha The Taste
		this.isFixedPartySize = this.venueId != null && this.priorityVenueIds.includes(this.venueId)
		if (this.isFixedPartySize) {
			this.partysize.set(1)
		}
		if (this.venueId) {
			this.store.dispatch(VenueActions.getOneVenue({ venueId: this.venueId }))
			this.store.dispatch(CheckinActions.loadAvailabilities({ venueId: this.venueId }))
		}
		this.store.select(getSelectedVenue).pipe(take(1)).subscribe()
	}

	confirmCheckin() {
		let updatedClient = {}

		if (!this.eulaFromClient() && this.eula()) {
			updatedClient = {
				...updatedClient,
				agreements: { eula: this.eula() },
			}
		}

		if (Object.keys(updatedClient).length > 0) {
			this.store.dispatch(ClientActions.updateClientBackend({ client: updatedClient }))
		}

		// Remover após campanha The Taste
		this.store.dispatch(
			CheckinActions.checkInConfirmation({
				checkInDate: this.checkinDate,
				partySize: this.isFixedPartySize ? 1 : this.partysize(),
			})
		)
	}

	okClick() {
		this.router.navigate(['/'])
	}

	cancelCheckin() {
		this.location.back()
	}
}
